import { useMemo } from "react"
import { DateTime } from "luxon"
import { useField } from "formik"
import { Stack, Typography } from "@mui/material"
import { LocalizedStrict } from "../../../../../../../../modules/localization/components/AppLocalized"
import { SelectedPriceListItem, CreateOrderDialogFlow } from "../../../../../createOrderTypes"
import { CreateUpdateDeliveryFormValues } from "../../CreateUpdateDeliveryTimePopover"
import { DeliveryTimesFieldArray } from "./DeliveryTimesFieldArray"
import { FormikSubmitButton } from "../../../../../../forms/Formik"
import { useDeliveryTimeLimits } from "./useDeliveryTimeLimits"
import { DeliveryTimeDay } from "../../../../form/formTypes"
import { DeliveryDatePicker } from "./DeliveryDatePicker"

type PopoverDeliveryTimeFormContentProps = Readonly<{
    priceList: SelectedPriceListItem
    flowConfig?: CreateOrderDialogFlow
    allowedWeekdays: number[]
    eventStartTime: DateTime | null
    isDisabledDatePicker: boolean
    closingDates: string[]
    openingHours?: DeliveryTimeDay[]
    maxAllowedDeliveries: number
    handlePopoverClose: () => void
}>

export function PopoverDeliveryTimeFormContent({
    priceList,
    flowConfig,
    allowedWeekdays,
    eventStartTime,
    isDisabledDatePicker,
    closingDates,
    openingHours = [],
    maxAllowedDeliveries,
    handlePopoverClose,
}: PopoverDeliveryTimeFormContentProps) {
    const [{ value: deliveryDate }] = useField<CreateUpdateDeliveryFormValues["deliveryDate"]>("deliveryDate")

    const { limits, noSlotsForDate } = useDeliveryTimeLimits(deliveryDate, openingHours)

    const showTimeWarningMessage = useMemo(() => {
        if (noSlotsForDate) return true
        if (!eventStartTime || !deliveryDate || !limits) return false

        const minBound = deliveryDate.set({ hour: limits.minTime.hour, minute: limits.minTime.minute })
        const maxBound = deliveryDate.set({ hour: limits.maxTime.hour, minute: limits.maxTime.minute })

        return eventStartTime < minBound || eventStartTime > maxBound
    }, [
        eventStartTime?.toISO(),
        deliveryDate?.toISO(),
        limits?.minTime.toISO(),
        limits?.maxTime.toISO(),
        noSlotsForDate,
    ])

    return (
        <Stack gap="2rem">
            <Stack gap="1.25rem">
                <LocalizedStrict id="create-order-delivery-times-add-delivery-time-title">
                    <Typography variant="h6">Add delivery time</Typography>
                </LocalizedStrict>

                <DeliveryDatePicker
                    closingDates={closingDates}
                    priceList={priceList}
                    flowConfig={flowConfig}
                    allowedWeekdays={allowedWeekdays}
                    disabledDatePicker={isDisabledDatePicker}
                    isShowTimeWarningMessage={showTimeWarningMessage}
                />

                <DeliveryTimesFieldArray
                    openingHours={openingHours}
                    showTimeWarningMessage={showTimeWarningMessage}
                    maxAllowedDeliveries={maxAllowedDeliveries}
                />
            </Stack>
            <Stack flexDirection="row" justifyContent="center">
                <LocalizedStrict
                    id={
                        showTimeWarningMessage
                            ? "create-order-delivery-times-dialog-close-button"
                            : "create-order-delivery-times-dialog-submit-button"
                    }
                >
                    <FormikSubmitButton
                        type={showTimeWarningMessage ? "button" : "submit"}
                        fullWidth={false}
                        onClick={() => (showTimeWarningMessage ? handlePopoverClose() : {})}
                    >
                        {showTimeWarningMessage ? "Ok" : "Confirm"}
                    </FormikSubmitButton>
                </LocalizedStrict>
            </Stack>
        </Stack>
    )
}
